import "./style.scss";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  toggleItem,
  toggleSidebar,
  toggleOpenCustomization,
  toggleCloseCustomization,
  chooseAll,
  clearAll,
} from "@/src/store/slices/SideBarMenu/SideBarMenuSlice";
import { RootState, AppDispatch } from "@/src/store";
import Arrow from "@/src/assets/sidebar/Arrow.png";
import BtnDefault from "../BtnDefault/BtnDefault";
import SearchSvg from "@/src/svg/default/SearchSvg";
import LogoSvg from "@/src/svg/default/LogoSvg";
import SidebarNav from "./SidebarNav/SidebarNav";
import SidebarNavSecond from "./SidebarNavSecond/SidebarNavSecond";
import { sideBarMenuFilters } from "@/src/constants/sidebar";
import { setActualUserConfig } from "@/src/store/slices/UserOptions/userOptionsSlice";
import { useEffect, useRef, useState } from "react";
import { userChangeInfoRequest } from "@/src/api/userInfoRequests";
import { prepareConfigForBackend } from "@/src/utils/userConfigStorage";
import { SidebarSvgExportConst } from "@/src/svg/sidebar/SidebarSvgExport";
import VerticalLine from "../VerticalLine/VerticalLine";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import {
  // TEMPORARY WITH All — switch to SIDEBAR_SITE_OPTIONS when restoring production (no All)
  SIDEBAR_SITE_SELECT_OPTIONS,
  getDefaultSidebarSiteUrl,
  toSidebarSiteDisplayValue,
  fromSidebarSiteDisplayValue,
} from "@/src/constants/accessControl";
import { setSelectedSiteUrl } from "@/src/store/slices/AccessControl/accessControlSlice";

const Sidebar = () => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [siderBarSecondleft, setSiderBarSecondleft] = useState<number | string>("-100%");
  // TEMPORARY WITH All. RESTORE WITHOUT All: [...SIDEBAR_SITE_OPTIONS]
  const siteOptions = [...SIDEBAR_SITE_SELECT_OPTIONS];
  const defaultSiteUrl = getDefaultSidebarSiteUrl();

  const dispatch = useDispatch<AppDispatch>();

  const customization = useSelector((state: RootState) => state.sideBarMenu.customization);
  const isSidebarOpen = useSelector((state: RootState) => state.sideBarMenu.isSidebarOpen);
  const sideBarMenuAllOptionsState = useSelector((state: RootState) => state.sideBarMenu.sideBarMenuAllOptionsState);
  const selectedIds = useSelector((state: RootState) => state.sideBarMenu.selectedIds);
  const userOptionsState = useSelector((state: RootState) => state.userOptions);
  const selectedSiteUrl = useSelector((state: RootState) => state.accessControl.selectedSiteUrl);
  const officeDisplayValue = toSidebarSiteDisplayValue(selectedSiteUrl);
  const officeAuto = useAutocomplete(siteOptions, officeDisplayValue);

  const userRole = localStorage.getItem("userRole") || "";
  // Исправление: парсим userID из JSON
  const userIDString = localStorage.getItem("userID");
  const userID = userIDString ? JSON.parse(userIDString) : null;

  // Флаг для предотвращения отправки при первой загрузке
  const isInitialMount = useRef(true);
  const prevUserOptionsState = useRef(userOptionsState);

  useEffect(() => {
    // Пропускаем первую загрузку (когда данные приходят с бека при логине)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevUserOptionsState.current = userOptionsState;
      return;
    }

    // Проверяем, действительно ли изменились данные
    const hasChanged = JSON.stringify(prevUserOptionsState.current) !== JSON.stringify(userOptionsState);

    if (!hasChanged) {
      return;
    }

    const updateUserConfig = async () => {
      // Проверяем, что userID существует
      if (!userID || !userOptionsState) return;

      try {
        // Подготавливаем конфигурацию для отправки на бэк (исключаем локальные настройки)
        const configToSend = prepareConfigForBackend(userOptionsState);

        // Вызываем функцию и ждем успешного ответа
        // Передаем полную конфигурацию из Redux для сохранения в localStorage после успешного ответа
        await userChangeInfoRequest.userConfigUpdate(userID, configToSend, userOptionsState);
        prevUserOptionsState.current = userOptionsState;
      } catch (error) {
        console.error("Error updating user config:", error);
        // Можно добавить уведомление пользователю об ошибке
      }
    };

    // Добавляем небольшую задержку (debounce), чтобы не делать запрос при каждом изменении
    const timeoutId = setTimeout(() => {
      updateUserConfig();
    }, 500); // 500ms задержка

    return () => clearTimeout(timeoutId);
  }, [userOptionsState, userID]);

  useEffect(() => {
    if (sidebarRef.current) {
      const width = sidebarRef.current.offsetWidth;
      setSiderBarSecondleft(`${width}px`);
    }
  }, [customization, isSidebarOpen]);

  useEffect(() => {
    if (officeAuto.query !== officeDisplayValue) {
      officeAuto.onSelect(officeDisplayValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync Redux → autocomplete once selectedSiteUrl changes
  }, [officeDisplayValue]);

  const commitOfficeSelection = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      officeAuto.onSelect(toSidebarSiteDisplayValue(defaultSiteUrl));
      dispatch(setSelectedSiteUrl(defaultSiteUrl));
      return;
    }

    const exact = siteOptions.find((opt) => opt === trimmed);
    if (exact) {
      officeAuto.onSelect(exact);
      dispatch(setSelectedSiteUrl(fromSidebarSiteDisplayValue(exact)));
      return;
    }

    officeAuto.onSelect(officeDisplayValue);
    dispatch(setSelectedSiteUrl(selectedSiteUrl));
  };

  const handleCloseCustomization = () => {
    dispatch(toggleCloseCustomization());
    dispatch(
      setActualUserConfig({
        SideBarConfig: {
          list: sideBarMenuAllOptionsState,
        },
      }),
    );
  };

  const hangleToggleSideBar = () => {
    if (customization) {
      dispatch(toggleCloseCustomization());
    }
    dispatch(toggleSidebar());
  };

  return (
    <>
      <div className={`sidebar ${isSidebarOpen ? "" : "close"}`} ref={sidebarRef}>
        <div className="sidebar-logo">
          <Link to={"/"}>
            <div className="sidebar-logo-svg">
              <LogoSvg />
            </div>
            <div className="sidebar-logo-line"></div>
            <div className="sidebar-logo-text">
              <p>Platforma</p>
            </div>
          </Link>
        </div>
        <div className="sidebar-search">
          <div className="sidebar-search-input">
            <SearchSvg />
            <input type="text" placeholder="Search everywhere" />
          </div>
          <BtnDefault onlyIcon={true}>
            <SearchSvg />
          </BtnDefault>
        </div>
        <div className="sidebar-office">
          <AutocompleteInput
            value={officeAuto.query}
            onChange={officeAuto.onChange}
            onSelect={(value) => {
              officeAuto.onSelect(value);
              dispatch(setSelectedSiteUrl(fromSidebarSiteDisplayValue(value)));
            }}
            isOpen={officeAuto.isOpen}
            options={officeAuto.filtered}
            placeholder="Choose Office"
            inputProps={{
              close: () => {
                commitOfficeSelection(officeAuto.query);
                officeAuto.close();
              },
              setOpen: officeAuto.onClick,
              onArrowClick: officeAuto.onArrowClick,
            }}
          />
        </div>
        <div className="sidebar-content">
          <SidebarNav
            title="Main menu"
            customization={customization}
            sideBarMenuAllOptions={sideBarMenuAllOptionsState.main}
            section="main"
          />
          <SidebarNav
            title="Tools"
            customization={customization}
            sideBarMenuAllOptions={sideBarMenuAllOptionsState.tools}
            section="tools"
          />
          {userRole === "admin" && (
            <SidebarNav
              title="Admin Panel"
              customization={customization}
              sideBarMenuAllOptions={sideBarMenuAllOptionsState.adminPanel}
              section="adminPanel"
            />
          )}
          <div className="sidebar-buttons">
            {customization ? (
              <div className="sidebar-buttons-content customization">
                <BtnDefault onlyIcon={isSidebarOpen} onClick={handleCloseCustomization}>
                  <p>Cancel</p>
                  <SidebarSvgExportConst.CancelSvg />
                </BtnDefault>
                <BtnDefault onlyIcon={isSidebarOpen} reverse={true} onClick={handleCloseCustomization}>
                  <p>Save</p>
                  <SidebarSvgExportConst.SaveSvg />
                </BtnDefault>
              </div>
            ) : (
              <div className="sidebar-buttons-content">
                <BtnDefault onlyIcon={true} onClick={hangleToggleSideBar}>
                  <img src={Arrow} alt="Arrow" style={isSidebarOpen ? {} : { transform: "rotate(180deg)" }} />
                </BtnDefault>
                <VerticalLine />
                <BtnDefault onlyIcon={isSidebarOpen} onClick={() => dispatch(toggleOpenCustomization())}>
                  <p>Customization</p>
                  <SidebarSvgExportConst.CustomizationSvg />
                </BtnDefault>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={`sidebar-second ${customization ? (isSidebarOpen ? "open" : "open close") : ""}`}
        style={{ left: customization ? siderBarSecondleft : "-100%" }}
      >
        <div className="sidebar-second-title">
          <p>Side menu</p>
          <span>Selected elements: {selectedIds.length}</span>
        </div>
        <div className="sidebar-second-buttons">
          <div className="sidebar-second-button choose" onClick={() => dispatch(chooseAll())}>
            <p>Choose all</p>
            <SidebarSvgExportConst.ChooseAllSvg />
          </div>
          <div className="sidebar-second-button clear" onClick={() => dispatch(clearAll())}>
            <p>Clear all</p>
            <SidebarSvgExportConst.ClearAllSvg />
          </div>
        </div>
        <div className="sidebar-second-content">
          <SidebarNavSecond
            title="Main menu"
            sideBarMenuFilters={sideBarMenuFilters.main}
            checkedIds={selectedIds}
            onToggle={(id) => dispatch(toggleItem(id))}
          />
          <SidebarNavSecond
            title="Tools"
            sideBarMenuFilters={sideBarMenuFilters.tools}
            checkedIds={selectedIds}
            onToggle={(id) => dispatch(toggleItem(id))}
          />
          <SidebarNavSecond
            title="Admin Panel"
            sideBarMenuFilters={sideBarMenuFilters.adminPanel}
            checkedIds={selectedIds}
            onToggle={(id) => dispatch(toggleItem(id))}
          />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
